import React from 'react';
import { Lock, ShieldCheck, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * FneLockBadge — Affiche un badge "verrouillé FNE" basé sur fneCertification.status.
 * Visuel + tooltip explicatif (raison du verrou pour OHADA + DGI).
 *
 * Props:
 *  - certification: object | null  → operation.fneCertification ou invoice.fneCertification
 *  - size: 'sm' | 'md' (défaut sm)
 *  - className: string
 */
const LOCK_REASONS = {
  certified: {
    label: '🔒 Facture verrouillée FNE',
    color: 'bg-amber-50 text-amber-800 border-amber-300',
    icon: ShieldCheck,
    tooltip:
      'Facture certifiée par la DGI Côte d\'Ivoire. Toute modification est interdite (OHADA + DGI). Pour corriger, émettez un avoir FNE.',
  },
  pending_fne: {
    label: '⏳ Certification FNE en cours',
    color: 'bg-blue-50 text-blue-800 border-blue-300',
    icon: AlertCircle,
    tooltip: 'Appel DGI en cours — l\'opération est temporairement verrouillée.',
  },
  refunded: {
    label: '↩️ Annulée par avoir FNE',
    color: 'bg-rose-50 text-rose-800 border-rose-300',
    icon: Lock,
    tooltip: 'Facture annulée par avoir DGI — modifications interdites.',
  },
  refunded_partial: {
    label: '↩️ Avoir partiel FNE',
    color: 'bg-rose-50 text-rose-800 border-rose-300',
    icon: Lock,
    tooltip: 'Avoir partiel émis — facture verrouillée pour toute modification.',
  },
  refunded_total: {
    label: '↩️ Annulée totalement (avoir FNE)',
    color: 'bg-rose-50 text-rose-800 border-rose-300',
    icon: Lock,
    tooltip: 'Facture totalement annulée par avoir DGI — verrouillée.',
  },
};

export const isFneLocked = (certification) => {
  const status = certification?.status;
  return Boolean(status && LOCK_REASONS[status]);
};

const FneLockBadge = ({ certification, size = 'sm', className = '' }) => {
  const status = certification?.status;
  const conf = status ? LOCK_REASONS[status] : null;
  if (!conf) return null;
  const Icon = conf.icon;
  const sizeCls = size === 'md' ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-1';
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${conf.color} ${sizeCls} font-semibold inline-flex items-center gap-1.5 cursor-help ${className}`}
            data-testid="fne-lock-badge"
          >
            <Icon className="w-3.5 h-3.5" />
            {conf.label}
            {certification?.reference ? (
              <span className="font-mono opacity-80 ml-1">{certification.reference}</span>
            ) : null}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs leading-relaxed">{conf.tooltip}</p>
          {certification?.certifiedAt ? (
            <p className="text-[10px] mt-1 opacity-70">
              Certifié le {new Date(certification.certifiedAt).toLocaleString('fr-FR')}
            </p>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FneLockBadge;
